import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, Package, Ship, Truck, Trash2 } from 'lucide-react';
import { Service, ROUTE_PATHS } from '@/lib/index';
import { motion } from 'framer-motion';
import { hoverLift } from '@/lib/motion';

interface ServiceCardProps {
  service: Service;
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'truck': Truck,
  'ship': Ship,
  'package': Package,
  'trash': Trash2,
};

export function ServiceCard({ service }: ServiceCardProps) {
  const IconComponent = iconMap[service.icon] || Package;

  return (
    <motion.div
      variants={hoverLift}
      initial="rest"
      whileHover="hover"
      className="h-full"
    >
      <Card className="h-full flex flex-col transition-all duration-200 border-border hover:border-accent hover:shadow-lg">
        <CardHeader>
          <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
            <IconComponent className="w-7 h-7 text-primary" />
          </div>
          <CardTitle className="text-xl font-semibold text-foreground">
            {service.title}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {service.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          <ul className="space-y-2">
            {service.features.slice(0, 3).map((feature, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </CardContent>
        <CardFooter>
          <Button
            asChild
            variant="ghost"
            className="w-full group hover:bg-accent/10 hover:text-accent"
          >
            <Link to={ROUTE_PATHS.SERVICES}>
              Learn More
              <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}